import {
  PRODUCT_GROUP_LEAF_FIELDS_FRAGMENT,
  PRODUCT_GROUP_SWATCH_EXPANSION_FRAGMENT,
  PRODUCT_COLOR_PATTERN_FIELDS_FRAGMENT,
} from "./fragments";

export const PRODUCT_CARD_GROUPING_NODES_QUERY = `
  ${PRODUCT_GROUP_LEAF_FIELDS_FRAGMENT}

  query ProductCardGroupingNodes($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Metaobject {
        id
        type
        handle

        ...ProductGroupLeafFields

        options: field(key: "product_group_options") {
          references(first: 250) {
            nodes {
              ... on Metaobject {
                id
                type
                handle

                ...ProductGroupLeafFields

                options: field(key: "product_group_options") {
                  references(first: 250) {
                    nodes {
                      ... on Metaobject {
                        id
                        type
                        handle

                        ...ProductGroupLeafFields
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const PRODUCT_SWATCH_COLOR_PATTERNS_QUERY = `
  ${PRODUCT_COLOR_PATTERN_FIELDS_FRAGMENT}

  query ProductSwatchColorPatterns($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        id
        title
        handle
        productType
        availableForSale
        tags

        featuredImage {
          url
          altText
        }

        images(first: 2) {
          nodes {
            url
            altText
          }
        }

        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
        }

        variants(first: 1) {
          nodes {
            id
          }
        }

        badges: metafield(namespace: "custom", key: "badge") {
          value
        }

        ...ProductColorPatternFields
      }
    }
  }
`;

export const PRODUCT_COLLECTION_MEMBERSHIP_QUERY = `
  query ProductCollectionMembership($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        id
        collections(first: 250) {
          nodes {
            id
          }
        }
      }
    }
  }
`;

export const PRODUCT_GROUP_SWATCH_EXPANSION_QUERY = `
  ${PRODUCT_GROUP_SWATCH_EXPANSION_FRAGMENT}
  ${PRODUCT_COLOR_PATTERN_FIELDS_FRAGMENT}

  query ProductGroupSwatchExpansion($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Metaobject {
        id
        ...ProductGroupSwatchExpansionFields
      }
    }
  }
`;

export const COLLECTION_FILTERS_QUERY = `
  ${PRODUCT_COLOR_PATTERN_FIELDS_FRAGMENT}

  query CollectionFilters(
    $handle: String!
    $filters: [ProductFilter!]
    $first: Int!
    $after: String
  ) {
    collection(handle: $handle) {
      id
      handle
      title

      excludeFilterGroups: metafield(
        namespace: "custom"
        key: "exclude_filter_groups"
      ) {
        value
      }

      products(
        first: $first
        after: $after
        filters: $filters
      ) {
        filters {
          id
          label
          type
          presentation

          values {
            id
            label
            count
            input

            image {
              image {
                url
                altText
              }

              previewImage {
                url
                altText
              }
            }

            swatch {
              color

              image {
                image {
                  url
                  altText
                }

                previewImage {
                  url
                  altText
                }
              }
            }
          }
        }

        nodes {
          id
          title
          handle
          productType
          availableForSale
          tags

          featuredImage {
            url
            altText
          }

          images(first: 2) {
            nodes {
              url
              altText
            }
          }

          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }

          variants(first: 1) {
            nodes {
              id
            }
          }

          productGroup: metafield(
            namespace: "custom"
            key: "product_group"
          ) {
            reference {
              ... on Metaobject {
                id
                handle
              }
            }
          }

          ...ProductColorPatternFields
        }

        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

export const SEARCH_FILTERS_QUERY = `
  ${PRODUCT_COLOR_PATTERN_FIELDS_FRAGMENT}

  query SearchFilters(
    $query: String!
    $filters: [ProductFilter!]
    $first: Int!
    $after: String
  ) {
    search(
      query: $query
      types: [PRODUCT]
      productFilters: $filters
      first: $first
      after: $after
    ) {
      productFilters {
        id
        label
        type
        presentation

        values {
          id
          label
          count
          input

          image {
            image {
              url
              altText
            }

            previewImage {
              url
              altText
            }
          }

          swatch {
            color

            image {
              image {
                url
                altText
              }

              previewImage {
                url
                altText
              }
            }
          }
        }
      }

      edges {
        node {
          ... on Product {
            id
            title
            handle
            productType
            availableForSale
            tags

            featuredImage {
              url
              altText
            }

            images(first: 2) {
              nodes {
                url
                altText
              }
            }

            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }

            variants(first: 1) {
              nodes {
                id
              }
            }

            productGroup: metafield(
              namespace: "custom"
              key: "product_group"
            ) {
              reference {
                ... on Metaobject {
                  id
                  handle
                }
              }
            }

            ...ProductColorPatternFields
          }
        }
      }

      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const COLLECTION_GROUPED_COUNT_QUERY = `
  query CollectionGroupedCount(
    $handle: String!
    $filters: [ProductFilter!]
    $first: Int!
    $after: String
  ) {
    collection(handle: $handle) {
      products(first: $first, after: $after, filters: $filters) {
        nodes {
          id
          productGroup: metafield(namespace: "custom", key: "product_group") {
            reference {
              ... on Metaobject {
                id
              }
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

export const SEARCH_GROUPED_COUNT_QUERY = `
  query SearchGroupedCount(
    $query: String!
    $filters: [ProductFilter!]
    $first: Int!
    $after: String
  ) {
    search(query: $query, types: [PRODUCT], productFilters: $filters, first: $first, after: $after) {
      edges {
        node {
          ... on Product {
            id
            productGroup: metafield(namespace: "custom", key: "product_group") {
              reference {
                ... on Metaobject {
                  id
                }
              }
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const SWATCH_PRODUCTS_FOR_FABRIC_MATCH_QUERY = `
  query SwatchProductsForFabricMatch($query: String!, $first: Int!) {
    search(query: $query, types: [PRODUCT], first: $first) {
      edges {
        node {
          ... on Product {
            id
            title
            handle
            availableForSale
            featuredImage {
              url
              altText
            }
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            variants(first: 1) {
              nodes {
                id
              }
            }
            fabric: metafield(namespace: "custom", key: "fabric") {
              reference {
                ... on Metaobject {
                  id
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const PRODUCT_VARIANTS_PAGE_QUERY = `
  query ProductVariantsPage($id: ID!, $first: Int!, $after: String) {
    product(id: $id) {
      options(first: 3) {
        name
      }
      variants(first: $first, after: $after) {
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          id
          availableForSale
          price {
            amount
          }
          compareAtPrice {
            amount
          }
          selectedOptions {
            name
            value
          }
          variantNote: metafield(namespace: "custom", key: "variant_note") {
            type
            value
          }
        }
      }
    }
  }
`;
